import { useState, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UserFilesBatchingActions, UserFilesBatchedOperationKind, UserFilesBatchedOperationStatus } from '@/user_files/batching/store/slice'
import { selectActiveUserFilesOperation, selectIsUserFilesOperationActive } from '@/user_files/batching/store/selectors'
import type { FileForSelection, FolderForSelection } from './useDriveSelection'
import type { FileSelectionPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'
import { buildFileSelectionPrecondition, buildFolderSelectionPrecondition } from '@/user_files/utils/preconditions'

type FileToDelete = {
  id: string
  name: string
  precondition: FileSelectionPrecondition
}

type FolderToDelete = {
  id: string
  name: string
  precondition: FolderSelectionPrecondition
}

type UseDeleteDialogParams = {
  files: FileForSelection[]
  folders: FolderForSelection[]
  currentFolderId: string | null
  selectedSelectableFileIds: string[]
  selectedFolderIds: string[]
  clearSelection: () => void
}

export function useDeleteDialog({ files, folders, currentFolderId, selectedSelectableFileIds, selectedFolderIds, clearSelection }: UseDeleteDialogParams) {
  const dispatch = useAppDispatch()
  const activeOperation = useAppSelector(selectActiveUserFilesOperation)
  const isAnyOperationActive = useAppSelector(selectIsUserFilesOperationActive)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [filesToDelete, setFilesToDelete] = useState<FileToDelete[]>([])
  const [foldersToDelete, setFoldersToDelete] = useState<FolderToDelete[]>([])
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const isDeleting =
    activeOperation?.kind === UserFilesBatchedOperationKind.DeleteSelection &&
    (activeOperation.status === UserFilesBatchedOperationStatus.Queued || activeOperation.status === UserFilesBatchedOperationStatus.Running)

  const openDeleteForSelected = useCallback(() => {
    setFilesToDelete(
      files
        .filter((f) => selectedSelectableFileIds.includes(f.id))
        .map((f) => ({ id: f.id, name: f.original_filename, precondition: buildFileSelectionPrecondition(f) }))
    )
    setFoldersToDelete(
      folders.filter((f) => selectedFolderIds.includes(f.id)).map((f) => ({ id: f.id, name: f.name, precondition: buildFolderSelectionPrecondition(f) }))
    )
    setDeleteDialogOpen(true)
  }, [files, folders, selectedSelectableFileIds, selectedFolderIds])

  const openDeleteForFile = useCallback(
    (fileId: string) => {
      const file = files.find((f) => f.id === fileId)
      if (!file) return
      setFilesToDelete([{ id: file.id, name: file.original_filename, precondition: buildFileSelectionPrecondition(file) }])
      setFoldersToDelete([])
      setDeleteDialogOpen(true)
    },
    [files]
  )

  const openDeleteForFolder = useCallback(
    (folderId: string) => {
      const folder = folders.find((f) => f.id === folderId)
      if (!folder) return
      setFilesToDelete([])
      setFoldersToDelete([{ id: folder.id, name: folder.name, precondition: buildFolderSelectionPrecondition(folder) }])
      setDeleteDialogOpen(true)
    },
    [folders]
  )

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false)
    setFilesToDelete([])
    setFoldersToDelete([])
    setDeleteError(null)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!currentFolderId) return
    if (filesToDelete.length === 0 && foldersToDelete.length === 0) return

    if (isAnyOperationActive) {
      setDeleteError('Another operation is in progress. Please wait.')
      return
    }

    setDeleteError(null)
    dispatch(
      UserFilesBatchingActions.enqueueDeleteSelection({
        sourceFolderId: currentFolderId,
        fileIds: filesToDelete.map((f) => f.id),
        folderIds: foldersToDelete.map((f) => f.id),
        filePreconditions: filesToDelete.map((f) => f.precondition),
        folderPreconditions: foldersToDelete.map((f) => f.precondition),
      })
    )
    clearSelection()
    closeDeleteDialog()
  }, [currentFolderId, filesToDelete, foldersToDelete, isAnyOperationActive, dispatch, clearSelection, closeDeleteDialog])

  return {
    deleteDialog: {
      open: deleteDialogOpen,
      files: filesToDelete,
      folders: foldersToDelete,
    },
    openDeleteForSelected,
    openDeleteForFile,
    openDeleteForFolder,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    deleteError,
  }
}
